import { useCallback } from "react";

import { usePostHog } from "posthog-js/react";
import { usePubNub } from "pubnub-react";
import { useLocation } from "react-router-dom";

import {
  useAuthContext,
  useChannelAPI,
  useIsDemoParticipant,
  useRoleContext,
} from "hooks";
import { messageSentCallbackValueIsFile } from "utils";

import { ChatConversation } from "./ChatConversation";
import { ChatConversationHeader } from "./components/ChatConversationHeader";
import { withCustomerAppSourceMeta } from "./utils";
import { ClientAppEvent } from "../../constants";

import type {
  MessageEnvelope,
  MessagePayload,
} from "@pubnub/react-chat-components";
import type { UriFileInput } from "pubnub";

// Canned demo counterparty reply. Placeholder copy pending product sign-off.
const DEMO_CHAT_CARRIER_REPLY =
  "Thanks for the message! We're on it and will keep you posted here. (sample demo reply)";
const DEMO_CHAT_CARRIER_SENDER_ID = "demo-carrier";
const DEMO_CHAT_CARRIER_SENDER_NAME = "Carrier (demo)";
const DEMO_CHAT_REPLY_DELAY_MS = 1000;

interface Props {
  currentChannel: string;
  channelGroup: UserChannelGroup;
  userPubnubData: PubnubClientData;
  onNewMessage?: (channelName: string | undefined) => void;
  useDesktopLayout: boolean;
  isStandaloneChat: boolean;
}

export function ChatConversationContainer(props: Props) {
  const {
    currentChannel,
    channelGroup,
    userPubnubData,
    onNewMessage: parentOnNewMessage = null,
    useDesktopLayout,
    isStandaloneChat,
  } = props;
  const { user } = useAuthContext();
  const { role } = useRoleContext();
  const isDemoParticipant = useIsDemoParticipant();
  const { updateTimeTokenForChannel } = useChannelAPI();
  const pubnubClient = usePubNub();
  const posthog = usePostHog();
  const location = useLocation();
  const { pathname } = location;

  const onMessageSent = useCallback(
    (value: MessagePayload | File | UriFileInput): void => {
      if (user == null) return;
      /*
    Had to use unknown instead of UriFileInput because for some reason
    '@pubnub/react-chat-components' does not export the UriFileInput interface
    */
      const isFile = messageSentCallbackValueIsFile(value);
      if (isFile) {
        /*
        Due to a bug in the message fetching, everytime a file is shared,
        the file is only downloadable after a subsequent message.
        This function sends a message after a file is sent and also reminds the
        user that files and messages have a set retention period.

        This message has an id because file messages do not get sent into webhooks.s
        Add the user id so that this message triggers an unread notification for the unread file.
      */
        // meta.source is stamped via withCustomerAppSourceMeta (single source of
        // truth; see its warning about keeping the backend payload shape in sync).
        pubnubClient.publish({
          channel: currentChannel,
          message: {
            text: "File shared. Files have 30 day in-conversation storage.",
            sender: {
              name: "Evotrux System",
            },
            meta: withCustomerAppSourceMeta({
              user_id: `user.${user.id}`,
              is_file: isFile,
            }),
          },
        });

        posthog?.capture(ClientAppEvent.INSTANT_CHAT_FILE_ATTACHEMENT_SENT, {
          path: pathname,
        });
      }

      if (!isFile && isDemoParticipant && role?.type === "SHIPPER") {
        /*
        In the sandbox demo the counterparty is automated and PubNub webhooks are
        no-op, so no carrier replies arrive. When the shipper prospect sends a
        message, publish a canned reply client-side (like the file-retention
        notice above) so they see a response. Gated to the shipper side so it
        never fires for a carrier sharing this conversation component.
        A non-user sender id renders it as an incoming message.
        */
        setTimeout(() => {
          pubnubClient.publish({
            channel: currentChannel,
            message: {
              text: DEMO_CHAT_CARRIER_REPLY,
              sender: {
                id: DEMO_CHAT_CARRIER_SENDER_ID,
                name: DEMO_CHAT_CARRIER_SENDER_NAME,
              },
            },
          });
        }, DEMO_CHAT_REPLY_DELAY_MS);
      }

      posthog?.capture(ClientAppEvent.INSTANT_CHAT_MESSAGE_SENT, {
        user_id: user.id,
        role_id: role?.id,
        role_type: role?.type,
        path: pathname,
      });
    },
    [
      user,
      role,
      currentChannel,
      posthog,
      pathname,
      pubnubClient,
      isDemoParticipant,
    ],
  );

  const onNewMessage = useCallback(
    (message: MessageEnvelope): void => {
      if (user == null) return;
      /*
      If the message received was in the same channel as the currentChannel,
      POST the message's timetoken to the backend to mark it as read.
    */
      if (
        message?.channel &&
        message?.timetoken &&
        message.channel === currentChannel
      ) {
        updateTimeTokenForChannel(message.channel, `${message.timetoken}`);
      }

      if (parentOnNewMessage) {
        parentOnNewMessage(message?.channel);
      }

      posthog?.capture(ClientAppEvent.INSTANT_CHAT_MESSAGE_RECEIVED, {
        user_id: user.id,
        role_id: role?.id,
        role_type: role?.type,
        path: pathname,
      });
    },
    [
      user,
      role,
      currentChannel,
      updateTimeTokenForChannel,
      parentOnNewMessage,
      posthog,
      pathname,
    ],
  );

  if (user == null) {
    return <p>Something went wrong</p>;
  }

  return (
    <ChatConversation
      currentChannel={currentChannel}
      channelGroup={channelGroup}
      user={user}
      userPubnubData={userPubnubData}
      onMessageSent={onMessageSent}
      conversationHeader={
        <ChatConversationHeader
          currentChannel={currentChannel}
          isShipmentDetailChat={isStandaloneChat}
        />
      }
      onNewMessage={onNewMessage}
      useDesktopLayout={useDesktopLayout}
      isStandaloneChat={isStandaloneChat}
    />
  );
}
